import { collection, query, where, Timestamp, getCountFromServer } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

const phCache = new Map<string, { data: unknown; ts: number }>();
const PH_CACHE_TTL = 5 * 60 * 1000;
const PH_LONG_TTL = 10 * 60 * 1000;

export function clearPhCache(): void {
  phCache.clear();
}

async function queryPH(queryType: string, { cacheKey, cacheTtl = PH_CACHE_TTL }: { cacheKey?: string; cacheTtl?: number } = {}): Promise<Record<string, unknown>> {
  if (cacheKey && phCache.has(cacheKey)) {
    const cached = phCache.get(cacheKey)!;
    if (Date.now() - cached.ts < cacheTtl) return cached.data as Record<string, unknown>;
    phCache.delete(cacheKey);
  }

  const fn = httpsCallable(functions, 'queryPostHog');
  const result = await fn({ queryType });
  const data = result.data as Record<string, unknown>;
  if (cacheKey) phCache.set(cacheKey, { data, ts: Date.now() });
  return data;
}

export async function getActiveStudentsToday(): Promise<number> {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const q = query(
    collection(db, 'progress'),
    where('lastSeen', '>=', Timestamp.fromDate(startOfDay))
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

export async function getTotalStudentCount(): Promise<number> {
  const q = query(
    collection(db, 'users'),
    where('role', '==', 'student')
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

export interface PostHogStats {
  counts: Record<string, number>;
  avgLoadMs: number | null;
  notConfigured?: boolean;
}

export async function fetchPostHogStats(): Promise<PostHogStats> {
  try {
    const countData = await queryPH('stats', { cacheKey: 'ph_counts_today' });
    const counts: Record<string, number> = {};
    for (const row of (countData.results || []) as [string, number][]) {
      counts[row[0]] = row[1];
    }

    let avgLoadMs: number | null = null;
    try {
      const loadData = await queryPH('avgLoad', { cacheKey: 'ph_avg_load' });
      avgLoadMs = ((loadData.results as unknown[])?.[0] as unknown[])?.[0] as number ?? null;
    } catch (_) { /* optional */ }

    return { counts, avgLoadMs };
  } catch (err) {
    if ((err as { code?: string })?.code === 'functions/failed-precondition') {
      return { counts: {}, avgLoadMs: null, notConfigured: true };
    }
    throw err;
  }
}

export function isPostHogConfigured(): boolean {
  return !!import.meta.env.VITE_POSTHOG_KEY;
}

const EN_TO_AR_PAGE: Record<string, string> = {
  'StudentHome': 'الصفحة الرئيسية',
  'StudentJourneyList': 'قائمة رحلات المادة',
  'StudentJourneyPage': 'صفحة الرحلة',
  'JourneyStepRunner': 'تنفيذ المحطة',
  'JourneyTamkeenSession': 'جلسة التمكين',
  'Profile': 'الملف الشخصي',
};

export interface PageLoadEntry {
  page: string;
  avgMs: number;
  count: number;
}

export async function fetchPageLoadRanking(): Promise<PageLoadEntry[] | null> {
  try {
    const data = await queryPH('pageLoad', { cacheKey: 'ph_page_ranking', cacheTtl: PH_LONG_TTL });
    const raw = ((data.results || []) as [string, number, number][]).map(([page, avgMs, cnt]) => ({
      page: EN_TO_AR_PAGE[page] || page,
      avgMs: avgMs ?? 0,
      count: cnt,
    }));

    const merged = new Map<string, PageLoadEntry>();
    for (const row of raw) {
      if (merged.has(row.page)) {
        const existing = merged.get(row.page)!;
        const totalCount = existing.count + row.count;
        existing.avgMs = (existing.avgMs * existing.count + row.avgMs * row.count) / totalCount;
        existing.count = totalCount;
      } else {
        merged.set(row.page, { ...row });
      }
    }

    return [...merged.values()]
      .map(r => ({ ...r, avgMs: Math.round(r.avgMs) }))
      .sort((a, b) => b.avgMs - a.avgMs);
  } catch (err) {
    if ((err as { code?: string })?.code === 'functions/failed-precondition') return null;
    throw err;
  }
}

export interface DeviceStats {
  mobile: number;
  desktop: number;
  mobilePercent: number;
  desktopPercent: number;
  total: number;
}

export async function fetchDeviceStats(): Promise<DeviceStats | null> {
  try {
    const data = await queryPH('devices', { cacheKey: 'ph_device_stats', cacheTtl: PH_LONG_TTL });
    const raw = ((data.results || []) as [string, number][]).map(([device, cnt]) => ({ device, count: cnt }));
    const total = raw.reduce((s, r) => s + r.count, 0);
    if (total === 0) return null;

    const mobile = raw
      .filter(r => ['Mobile', 'Tablet'].includes(r.device))
      .reduce((s, r) => s + r.count, 0);
    const desktop = total - mobile;

    return {
      mobile,
      desktop,
      mobilePercent: Math.round((mobile / total) * 100),
      desktopPercent: Math.round((desktop / total) * 100),
      total,
    };
  } catch (err) {
    if ((err as { code?: string })?.code === 'functions/failed-precondition') return null;
    throw err;
  }
}

export async function fetchSentryStats(): Promise<unknown> {
  const fn = httpsCallable(functions, 'getSentryIssues');
  const result = await fn();
  return result.data;
}
